import { useQuery } from '@tanstack/react-query';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, useTheme,
} from '@mui/material';
import { getCityUsage, getCurrencyUsage, getStateUsage, getTimezoneUsage } from '../../services/platform/masterService';

type UsageKind = 'state' | 'city' | 'currency' | 'timezone';

interface Props {
  kind?: UsageKind;
  target: { _id: string; name: string; code: string; isActive?: boolean } | null;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  onDeactivate: () => void;
}

const COPY: Record<UsageKind, { noun: string; checking: string; none: string }> = {
  state: { noun: 'state / province', checking: 'cities, zones and addresses', none: 'No cities, zones or addresses use this state.' },
  city: { noun: 'city', checking: 'zones and addresses', none: 'No zones or addresses use this city.' },
  currency: {
    noun: 'currency',
    checking: 'countries, settings, payments and wallets',
    none: 'No countries, currency settings, payments or wallets use this currency.',
  },
  timezone: {
    noun: 'timezone',
    checking: 'countries, settings, zones and calendars',
    none: 'No countries, tenant settings, operational zones or working calendars use this timezone.',
  },
};

interface UsageSummary {
  rows: Array<readonly [string, number]>;
  total: number;
  /** Set when the record can be neither deleted nor deactivated. */
  lockedMessage?: string;
}

async function loadUsage(kind: UsageKind, id: string): Promise<UsageSummary> {
  if (kind === 'timezone') {
    const u = await getTimezoneUsage(id);
    return {
      lockedMessage: u.total > 0
        ? 'A timezone in use cannot be deleted or deactivated — switch those records to another timezone first.'
        : undefined,
      total: u.total,
      rows: ([
        ['Tenant default timezone', u.tenantDefault ? 1 : 0],
        ['Default Timezone system setting', u.systemSetting ? 1 : 0],
        ['Countries (default timezone)', u.countries],
        ['Operational zones', u.zones],
        ['Working calendars', u.workingCalendars],
      ] as const).filter(([, n]) => n > 0),
    };
  }
  if (kind === 'currency') {
    const u = await getCurrencyUsage(id);
    return {
      lockedMessage: u.isBase
        ? 'The base currency cannot be deleted or deactivated — change the base currency in Locale settings first.'
        : undefined,
      total: u.total,
      rows: ([
        ['Platform base currency', u.isBase ? 1 : 0],
        ['Tenant default currency', u.tenantDefault ? 1 : 0],
        ['Countries (default / secondary currency)', u.countries],
        ['Display / settlement / reporting settings', u.currencyProfile],
        ['Payments', u.payments],
        ['Wallets', u.wallets],
      ] as const).filter(([, n]) => n > 0),
    };
  }
  const u = kind === 'city' ? await getCityUsage(id) : await getStateUsage(id);
  return {
    total: u.total,
    rows: ([
      ['Cities', u.cities ?? 0],
      ['Operational zones', u.zones],
      ['Dispatch zones', u.dispatchZones],
      ['Saved customer addresses', u.addresses],
    ] as const).filter(([, n]) => n > 0),
  };
}

/** Delete confirmation for masters with dependants — checks usage first and offers deactivation when in use. */
export default function StateDeleteDialog({ kind = 'state', target, loading, error, onCancel, onConfirm, onDeactivate }: Props) {
  const theme = useTheme();
  const copy = COPY[kind];
  const { data: usage, isLoading, isError } = useQuery({
    queryKey: [`${kind}-usage`, target?._id],
    queryFn: () => loadUsage(kind, target!._id),
    enabled: !!target,
    staleTime: 0,
  });

  const inUse = (usage?.total ?? 0) > 0;
  const canDelete = !!usage && !inUse && !isError;

  return (
    <Dialog open={!!target} onClose={onCancel} maxWidth="xs" fullWidth sx={{ zIndex: (t) => t.zIndex.modal + 2 }}>
      <DialogTitle sx={{ fontWeight: 650, borderTop: `3px solid ${theme.palette.error.main}` }}>
        Delete {copy.noun}?
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          <strong>{target?.name}</strong> ({target?.code})
        </Typography>
        {isLoading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2">Checking {copy.checking} that use it…</Typography>
          </Stack>
        )}
        {isError && <Alert severity="error">Could not check where this {copy.noun} is used. Try again.</Alert>}
        {usage && inUse && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight={600} gutterBottom>This {copy.noun} is in use and cannot be deleted:</Typography>
            {usage.rows.map(([label, n]) => (
              <Typography key={label} variant="body2">• {label}{n > 1 || !/(currency|timezone|setting)$/.test(label) ? `: ${n}` : ''}</Typography>
            ))}
            <Typography variant="body2" sx={{ mt: 1 }}>
              {usage.lockedMessage
                || 'Deactivate it instead — it stays linked to existing records but is hidden from new selections.'}
            </Typography>
          </Alert>
        )}
        {canDelete && (
          <Alert severity="info">
            {copy.none} Deleting it permanently removes it from the list.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
        <Button onClick={onCancel}>Cancel</Button>
        {inUse ? (
          <Button
            variant="contained"
            color="warning"
            disabled={loading || target?.isActive === false || !!usage?.lockedMessage}
            onClick={onDeactivate}
          >
            {target?.isActive === false ? 'Already inactive' : 'Deactivate instead'}
          </Button>
        ) : (
          <Button variant="contained" color="error" disabled={!canDelete || loading} onClick={onConfirm}>
            Delete permanently
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
